import React, { useEffect, useState } from "react";
import { Link, NavLink, useNavigate, useParams } from "react-router-dom";
import Select from "react-select";
import { Button, Modal } from "react-bootstrap";
import { Variables } from "../../Variations/Variations";
import { toast } from "react-toastify";
import DataTable from "react-data-table-component";
import {
  MdOutlineAssignmentReturned,
  MdOutlineDelete,
  MdManageSearch,
} from "react-icons/md";
import { AiOutlineRight } from "react-icons/ai";

const ManageWidget = () => {
  const { WidgetID } = useParams();
  const navigate = useNavigate();

  // ---- State ----
  const [name, setName] = useState("");
  const [url, setUrl] = useState("");
  const [allCategories, setAllCategories] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);

  const [unassignedAttributeValues, setUnassignedAttributeValues] = useState(
    []
  );
  const [
    filteredUnassignedAttributeValues,
    setFilteredUnassignedAttributeValues,
  ] = useState([]);
  const [assignedAttributeValues, setAssignedAttributeValues] = useState([]);
  const [filteredAssignedAttributeValues, setFilteredAssignedAttributeValues] =
    useState([]);
  const [isOpenUnAssignedAttributeValues, setIsOpenUnAssignedAttributeValues] =
    useState(false);

  // ---- react-select setup ----
  const categoryOptions = allCategories.map((cat) => ({
    value: cat.CategoryId,
    label: cat.CategoryName,
  }));
  const selectedCategoryOptions = (selectedCategories || []).map((cat) => ({
    value: cat.CategoryId,
    label: cat.CategoryName,
  }));

  // ---- DataTable columns ----
  const unassignedCols = [
    { name: "ID", selector: (row) => row.AttributeValueId, sortable: true },
    {
      name: "Attribute Value",
      selector: (row) => row.AttributeValueName,
      sortable: true,
    },
    { name: "Attribute", selector: (row) => row.Attribute1, sortable: true },
    {
      name: "Assign",
      cell: (row) => (
        <button
          className="btn btn-outline-secondary btn-sm"
          onClick={() => assignAttributeValue(row)}
        >
          <MdOutlineAssignmentReturned />
        </button>
      ),
    },
  ];
  const assignedCols = [
    { name: "ID", selector: (row) => row.AttributeValueId, sortable: true },
    {
      name: "Attribute Value",
      selector: (row) => row.AttributeValueName,
      sortable: true,
    },
    { name: "Attribute", selector: (row) => row.Attribute1, sortable: true },
    {
      name: "Unassign",
      cell: (row) => (
        <button
          className="btn btn-outline-danger btn-sm"
          onClick={() => unassignAttributeValue(row.AttributeValueId)}
        >
          <MdOutlineDelete />
        </button>
      ),
    },
  ];

  // ---- Fetch functions ----
  async function fetchUnassignedAttributeValues() {
    const res = await fetch(
      `${Variables.API_URL}Widgets/GetUnAssignedAttributeValues/${WidgetID}`
    );
    if (res.ok) {
      const list = await res.json();
      setUnassignedAttributeValues(list);
      setFilteredUnassignedAttributeValues(list);
    }
  }

  async function fetchAssignedAttributeValues() {
    const res = await fetch(
      `${Variables.API_URL}Widgets/GetAssignedAttributeValues/${WidgetID}`
    );
    if (res.ok) {
      const list = await res.json();
      setAssignedAttributeValues(list);
      setFilteredAssignedAttributeValues(list);
    }
  }

  async function fetchAssignedCategories() {
    const res = await fetch(
      `${Variables.API_URL}Widgets/GetAssignedCategories/${WidgetID}`
    );
    if (res.ok) {
      const list = await res.json();
      setSelectedCategories(list || []);
    }
  }

  async function loadWidget(id) {
    const res = await fetch(`${Variables.API_URL}Widgets/GetWidget/${id}`);
    if (res.ok) {
      const data = await res.json();
      setName(data.Title);
      setUrl(data.Url);
      setSelectedCategories(data.Categories || []);
    }
  }

  async function loadCategories() {
    const res = await fetch(`${Variables.API_URL}Category/GetCategories`);
    if (res.ok) {
      setAllCategories(await res.json());
    }
  }

  // ---- Assign / Unassign handlers ----
  async function assignAttributeValue(row) {
    const res = await fetch(
      `${Variables.API_URL}Widgets/AssignAttributeValueToWidget`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          AttributeValueId: row.AttributeValueId,
          WidgetId: WidgetID,
        }),
      }
    );
    const msg = await res.text();
    if (res.ok) {
      toast.success(msg);
      await fetchUnassignedAttributeValues();
      await fetchAssignedAttributeValues();
      setIsOpenUnAssignedAttributeValues(false);
    } else {
      toast.error(msg);
    }
  }

  async function unassignAttributeValue(id) {
    const res = await fetch(
      `${Variables.API_URL}Widgets/UnassignAttributeValueToWidget`,
      {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ AttributeValueId: id, WidgetId: WidgetID }),
      }
    );
    const msg = await res.text();
    if (res.ok) {
      toast.success(msg);
      await fetchAssignedAttributeValues();
      await fetchUnassignedAttributeValues();
    } else {
      toast.error(msg);
    }
  }

  // ---- Search filters ----
  function handleSearchUnassignedValues(e) {
    const v = e.target.value.toLowerCase();
    setFilteredUnassignedAttributeValues(
      unassignedAttributeValues.filter((i) =>
        i.AttributeValueName.toLowerCase().includes(v)
      )
    );
  }

  function handleSearchAssignedValues(e) {
    const v = e.target.value.toLowerCase();
    setFilteredAssignedAttributeValues(
      assignedAttributeValues.filter((i) =>
        i.AttributeValueName.toLowerCase().includes(v)
      )
    );
  }

  // ---- Validation ----
  function isValid() {
    if (!name.trim()) {
      toast.error("Widget Title Required");
      return false;
    }
    if (!selectedCategories.length) {
      toast.error("Please assign at least one category");
      return false;
    }
    return true;
  }

  // ---- Save (Add / Update) ----
  async function handleSubmit(e) {
    e.preventDefault();
    if (!isValid()) return;

    try {
      let widgetId;

      // Add vs Update
      if (WidgetID === "0") {
        const addRes = await fetch(`${Variables.API_URL}Widgets/AddWidget`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            Title: name,
            Url: url,
            IsPublished: false,
          }),
        });
        if (!addRes.ok) {
          throw new Error(await addRes.text());
        }
        widgetId = await addRes.json();
        toast.success("Widget added successfully");
      } else {
        const updRes = await fetch(
          `${Variables.API_URL}Widgets/UpdateWidget/${WidgetID}`,
          {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ Title: name, Url: url }),
          }
        );
        if (!updRes.ok) {
          throw new Error(await updRes.text());
        }
        widgetId = WidgetID;
        toast.success("Widget updated successfully");
      }

      // Assign categories
      const assignRes = await fetch(
        `${Variables.API_URL}Widgets/AssignCategoryToWidget/${widgetId}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(
            selectedCategories.map((cat) => ({
              WidgetId: widgetId,
              CategoryId: cat.CategoryId,
            }))
          ),
        }
      );
      if (!assignRes.ok) {
        throw new Error("Failed to assign categories");
      }

      navigate(WidgetID === "0" ? `/admin/gazelle/Widgets/${widgetId}` : "/admin/gazelle/Widgets");
    } catch (err) {
      toast.error(err.message);
    }
  }

  // ---- Initial load ----
  useEffect(() => {
    loadCategories();
    if (WidgetID !== "0") {
      loadWidget(WidgetID);
      fetchAssignedCategories();
      fetchUnassignedAttributeValues();
      fetchAssignedAttributeValues();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- Render ----
  return (
    <div className="rightSide">
      <div className="container-fluid mt-3">
        {/* Header */}
        <div className="row mb-3">
          <div className="col-12">
            <h2>{WidgetID === "0" ? "Add" : `Manage ${name}`} Widget</h2>
            <ul className="portalPath">
              <li>
                Home <AiOutlineRight />
              </li>
              <li>
                <Link to="/admin/gazelle/Widgets">
                  Widgets <AiOutlineRight />
                </Link>
              </li>
              <li>Manage Widget</li>
            </ul>
          </div>
        </div>

        {/* Form */}
        <div className="row">
          <div className="col-12">
            <div className="card">
              <div className="card-body">
                <div className="d-flex justify-content-end mb-3">
                  <NavLink to="/admin/gazelle/Widgets" className="btn btn-secondary me-2">
                    Cancel
                  </NavLink>
                  <button className="btn btn-primary" onClick={handleSubmit}>
                    Save
                  </button>
                </div>

                <div className="row gy-3">
                  <div className="col-lg-6">
                    <label className="mb-2">
                      Widget Title <span className="required">*</span>
                    </label>
                    <input
                      id="name"
                      className="form-control"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Widget Title"
                    />
                  </div>
                  <div className="col-lg-6">
                    <label className="mb-2">
                      Widget URL <span className="required">*</span>
                    </label>
                    <input
                      id="url"
                      className="form-control"
                      value={url}
                      onChange={(e) => setUrl(e.target.value)}
                      placeholder="Widget URL"
                    />
                  </div>
                  <div className="col-lg-12">
                    <label className="mb-2">Categories</label>
                    <Select
                      isMulti
                      options={categoryOptions}
                      value={selectedCategoryOptions}
                      placeholder="Select categories…"
                      onChange={(opts) =>
                        setSelectedCategories(
                          (opts || []).map((o) => ({
                            CategoryId: o.value,
                            CategoryName: o.label,
                          }))
                        )
                      }
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Attribute Values Section */}
            {WidgetID !== "0" && (
              <div className="card mt-4">
                <div className="card-header d-flex justify-content-between">
                  <h5 className="card-title">Assigned Attribute Values</h5>
                  <button
                    className="btn btn-primary"
                    onClick={() => setIsOpenUnAssignedAttributeValues(true)}
                  >
                    Assign Attribute Value
                  </button>
                </div>
                <div className="card-body">
                  <div className="row mb-3">
                    <div className="col-8" />
                    <div className="col-4 position-relative">
                      <MdManageSearch className="position-absolute top-50 translate-middle-y ms-2" />
                      <input
                        type="text"
                        className="form-control form-control-sm ps-5"
                        placeholder="Search…"
                        onChange={handleSearchAssignedValues}
                      />
                    </div>
                  </div>
                  <DataTable
                    columns={assignedCols}
                    data={filteredAssignedAttributeValues}
                    pagination
                  />
                </div>

                {/* Assign Modal */}
                <Modal
                  dialogClassName="w-max"
                  show={isOpenUnAssignedAttributeValues}
                  onHide={() => setIsOpenUnAssignedAttributeValues(false)}
                >
                  <Modal.Header closeButton>
                    <Modal.Title>Assign Attribute Values</Modal.Title>
                  </Modal.Header>
                  <Modal.Body>
                    <div className="position-relative mb-3">
                      <MdManageSearch className="position-absolute top-50 translate-middle-y ms-2" />
                      <input
                        type="text"
                        className="form-control form-control-sm ps-5"
                        placeholder="Search…"
                        onChange={handleSearchUnassignedValues}
                      />
                    </div>
                    <DataTable
                      columns={unassignedCols}
                      data={filteredUnassignedAttributeValues}
                      pagination
                    />
                  </Modal.Body>
                  <Modal.Footer>
                    <Button
                      variant="secondary"
                      onClick={() => setIsOpenUnAssignedAttributeValues(false)}
                    >
                      Close
                    </Button>
                  </Modal.Footer>
                </Modal>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManageWidget;
